import React from 'react';
import { Card, Col, Progress, Row, Statistic, Tag, Typography } from 'antd';
import { labelBlocker } from './executionLabels';
import type {
  ExecutionProgressProjection,
  RouteStatusProjection,
  VehicleCargoProjection
} from '../routes/execution/dashboardProjection';
import { buildVehicleCargoProgressLines } from '../routes/execution/dashboardProjection/vehicleCargoProjection';

const { Text } = Typography;

type Props = {
  routeStatus: RouteStatusProjection;
  progress: ExecutionProgressProjection | null;
  vehicleCargo: VehicleCargoProjection;
  compact?: boolean;
};

export const ExecutionProgressWidgets: React.FC<Props> = ({
  routeStatus,
  progress,
  vehicleCargo,
  compact = false
}) => {
  const cargoLines = buildVehicleCargoProgressLines(vehicleCargo);
  const blockers = progress?.blockers ?? [];

  if (!progress) {
    return (
      <Card size="small" title="Прогресс исполнения">
        <Text type="secondary">Данные исполнения пока недоступны.</Text>
      </Card>
    );
  }

  return (
    <Card
      size="small"
      title="Прогресс исполнения"
      extra={<Tag color={routeStatus.color}>{routeStatus.label}</Tag>}
      style={{ marginBottom: compact ? 8 : 16 }}
    >
      <Progress percent={progress.routePercent} status={blockers.length ? 'active' : 'normal'} />

      <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
        <Col xs={12} md={6}>
          <Statistic title="Погрузка" value={progress.loadingPercent} suffix="%" valueStyle={{ fontSize: compact ? 16 : 20 }} />
        </Col>
        <Col xs={12} md={6}>
          <Statistic title="Выгрузка" value={progress.unloadingPercent} suffix="%" valueStyle={{ fontSize: compact ? 16 : 20 }} />
        </Col>
        <Col xs={12} md={6}>
          <Statistic
            title="Точки"
            value={progress.stopsCompleted}
            suffix={`/ ${progress.stopsTotal}`}
            valueStyle={{ fontSize: compact ? 16 : 20 }}
          />
        </Col>
        <Col xs={12} md={6}>
          <Statistic title="В машине" value={vehicleCargo.inVehiclePlaces} suffix="мест" valueStyle={{ fontSize: compact ? 16 : 20 }} />
        </Col>
      </Row>

      {cargoLines.loadedLines.length > 0 ? (
        <div style={{ marginTop: 12 }}>
          <Text strong style={{ fontSize: 12 }}>
            В машине / загружено:
          </Text>
          {cargoLines.loadedLines.map((line) => (
            <Text key={line} style={{ display: 'block', fontSize: 12 }}>
              · {line}
            </Text>
          ))}
        </div>
      ) : null}

      {cargoLines.remainingLines.length > 0 ? (
        <div style={{ marginTop: 8 }}>
          <Text strong style={{ fontSize: 12 }}>
            Осталось довезти:
          </Text>
          {cargoLines.remainingLines.map((line) => (
            <Text key={line} style={{ display: 'block', fontSize: 12 }}>
              · {line}
            </Text>
          ))}
        </div>
      ) : null}

      {(progress.partialCargoCount ?? 0) > 0 || (progress.failedCargoCount ?? 0) > 0 ? (
        <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
          Частично: {progress.partialCargoCount} · Ошибки: {progress.failedCargoCount}
        </Text>
      ) : null}

      {blockers.length > 0 ? (
        <div style={{ marginTop: 12 }}>
          {blockers.map((b) => (
            <Tag key={b} color="orange">
              {labelBlocker(b)}
            </Tag>
          ))}
        </div>
      ) : null}
    </Card>
  );
};

export default ExecutionProgressWidgets;
